<template>
  <div class="form-wrapper" :class="[mode]">
    <div class="form">
      <div class="form-header">
        <div class="title" ref="title">
          Address {{ index + 1 }}
          <div class="tooltip-wrapper">
            <tooltip-component v-if="address.id > 0 && mode === FormModes.view"
              ><template #toggle>&nbsp;<icon icon="info" /></template>
              <div>
                This address has already been saved and cannot be deleted. You can still make edits
                by clicking "edit".
              </div></tooltip-component
            >
          </div>
        </div>
        <div class="actions">
          <a
            v-if="address.id <= 0"
            :disabled="disabled"
            @click="handleEditOrDelete(address, index)"
            >{{ mode === FormModes.view ? "edit" : "delete" }}</a
          >
          <a
            v-if="address.id > 0 && mode === FormModes.view"
            :disabled="disabled"
            @click="changeMode(FormModes.edit)"
            >edit</a
          >
        </div>
      </div>
      <div
        v-show="mode === FormModes.view"
        class="summary"
        :class="{ isCollapsed: mode === FormModes.view && !showAddressDetails }"
      >
        <span class="address-formatted">{{ formattedAddress }} </span>
        <div class="details-toggle" @click="showAddressDetails = !showAddressDetails">
          {{ showAddressDetails ? "Hide address details" : "View address details" }}
          <font-awesome-icon
            :icon="['fa-solid', showAddressDetails ? 'fa-angle-up' : 'fa-angle-down']"
            class="icon"
          />
        </div>
      </div>
      <SlideTransition :is-visible="showFields">
        <div class="form-fields" id="address" v-show="showFields">
          <div class="row">
            <div class="col-12 col-mobile-12">
              <VeeField name="address1" v-slot="{ field }" :value="address.address1">
                <base-form-field
                  v-model="address.address1"
                  v-bind="field"
                  :schema="addressSchema.address1"
                  :disabled="!canEdit || isFormDisabled"
                >
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="address1" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
          </div>
          <div class="col-group" style="">
            <div class="col-lg-6 col-md-6 col-mobile-12">
              <VeeField
                name="addressUnitTypeId"
                v-slot="{ field }"
                :value="address.addressUnitTypeId"
              >
                <base-form-field
                  v-model.number="address.addressUnitTypeId"
                  v-bind="field"
                  :schema="addressSchema.addressUnitTypeId"
                  :options="unitTypes"
                  :disabled="!canEdit || isFormDisabled"
                  :class="{
                    'is-invalid':
                      !addressSchema.addressUnitTypeId.valid && errors?.addressUnitTypeId,
                  }"
                  @update:model-value="
                    handleChange($event, addressSchema.addressUnitTypeId, unitTypes)
                  "
                >
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="addressUnitTypeId" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
            <div class="col-lg-6 col-md-6 col-mobile-12">
              <VeeField name="address2" v-slot="{ field }" :value="address.address2">
                <base-form-field
                  v-model="address.address2"
                  v-bind="field"
                  autocomplete="address-line2"
                  :schema="addressSchema.address2"
                  :disabled="!canEdit || isFormDisabled"
                  :class="{
                    'is-invalid': !addressSchema.address2.valid && errors?.address2,
                  }"
                >
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="address2" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
          </div>
          <div class="col-group">
            <div class="col-lg-6 col-md-6 col-mobile-12">
              <VeeField name="city" v-slot="{ field }" :value="address.city">
                <base-form-field
                  v-model="address.city"
                  v-bind="field"
                  :schema="addressSchema.city"
                  :disabled="!canEdit || isFormDisabled"
                  :class="{
                    'is-invalid': !addressSchema.city.valid && errors?.city,
                  }"
                >
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="city" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
            <div class="col-lg-6 col-md-6 col-mobile-12">
              <VeeField name="stateId" v-slot="{ field }" :value="address.stateId">
                <base-form-field
                  v-model.number="address.stateId"
                  v-bind="field"
                  :schema="addressSchema.stateId"
                  :options="states"
                  :disabled="!canEdit || isFormDisabled"
                  :class="{
                    'is-invalid': !addressSchema.stateId.valid && errors?.stateId,
                  }"
                  @update:model-value="handleChange($event, addressSchema.stateId, states)"
                >
                  <template #default-option> State </template>
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="stateId" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
          </div>
          <div class="col-group">
            <div class="col-lg-6 col-md-6 col-mobile-12">
              <VeeField name="postalCode" v-slot="{ field }" :value="address.postalCode">
                <base-form-field
                  v-model="address.postalCode"
                  v-bind="field"
                  :schema="addressSchema.postalCode"
                  :disabled="!canEdit || isFormDisabled"
                  :class="{
                    'is-invalid': !addressSchema.postalCode.valid && errors?.postalCode,
                  }"
                >
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="postalCode" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
            <div class="col-lg-6 col-md-6 col-mobile-12">
              <VeeField name="countryId" v-slot="{ field }" :value="address.countryId">
                <base-form-field
                  v-model.number="address.countryId"
                  v-bind="field"
                  :schema="addressSchema.countryId"
                  :options="countries"
                  :disabled="!canEdit || isFormDisabled"
                  @update:model-value="handleChange($event, addressSchema.countryId, countries)"
                  :class="{
                    'is-invalid': !addressSchema.countryId.valid && errors?.countryId,
                  }"
                >
                  <template #default-option> Country </template>
                  <template #errors>
                    <ErrorMessage v-show="mode !== 'view'" name="countryId" class="error" />
                  </template>
                </base-form-field>
              </VeeField>
            </div>
          </div>
        </div>
      </SlideTransition>
      <div
        class="footer"
        style="display: block"
        :disabled="!canEdit || isFormDisabled"
        v-if="mode === FormModes.add || mode === FormModes.edit"
      >
        <next-button
          class="sc-button"
          text="Save Address"
          @click="confirmChanges"
          :disabled="savingChanges"
          :loading="savingChanges"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  defineProps,
  toRefs,
  withDefaults,
  ref,
  onMounted,
  defineExpose,
  defineEmits,
  watch,
  onUnmounted,
} from "vue";
import { FieldSchema, Option } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import NextButton from "@/components/shared/NextButton.vue";
import { Applicant, Address, AddressFormInputs } from "@/models/application";
import { Stipulation } from "@/models/opal";
import { FormModes } from "@/models/form";
import { Field as VeeField, ErrorMessage, configure } from "vee-validate";
import useAddressForm, { formatAddress } from "@/composables/forms/useAddressForm";
import SlideTransition from "@/components/transitions/SlideTransition.vue";
import TooltipComponent from "@/components/shared/TooltipComponent.vue";
import Icon from "@/components/shared/Icon.vue";

const store = useStore();

interface Props {
  address: AddressFormInputs;
  addresses?: any[];
  previousAddresses?: any[];
  applicant: Applicant;
  stipulation: Stipulation;
  index: number;
  disabled: boolean;
  loading: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});

const emit = defineEmits<{
  (e: "closeDialogueModal"): void;
  (e: "onDelete", payload: { index: number; address: AddressFormInputs }): void;
  (e: "onError"): void;
}>();

configure({
  // Stops validation when model value changes (input)
  validateOnModelUpdate: true,
  // Prevents validation when the component is first mounted
  validateOnInput: true,
  // Optionally turn off blur/change if needed
  validateOnBlur: true,
  validateOnChange: true,
});

const { address, previousAddresses } = toRefs(props);

const isMounted = ref<boolean>(false);

const isFormDisabled = computed(() => {
  return props.disabled || mode.value === FormModes.view;
});

const {
  addressSchema,
  countries,
  error,
  errors,
  formattedAddress,
  getOption,
  mode,
  states,
  unitTypes,
  resetForm,
  resetAddressSchema,
  onValidate,
  savingChanges,
  saveAddressAsync,
  showAddressDetails,
  showFields,
  statesData,
  values,
  validate,
  validating,
} = useAddressForm(
  {
    formData: address,
    disabled: isFormDisabled,
    previousAddresses,
    isMounted,
  },
  store,
);

const canEdit = computed<boolean>(() => {
  return !props.disabled || !savingChanges.value || mode.value !== FormModes.view;
});

const hasErrors = computed(() => Object.keys(errors.value)?.length > 0);

async function handleChange(event: number | string, schema: FieldSchema, options?: Option[]) {
  const value = event;
  const option = options ? getOption(value, options) : undefined;

  switch (schema.name) {
    case "addressUnitTypeId":
      address.value.addressUnitTypeName = option?.label;
      break;
    case "stateId":
      address.value.stateName = option?.label;
      address.value.stateAbbreviation = statesData.value.find(
        (s: any) => s.id === value,
      )?.abbreviation;
      break;
    case "countryId":
      address.value.countryName = option?.label;
      break;
    default:
      break;
  }
}

function handleEditOrDelete(address: AddressFormInputs, index: number) {
  if (canEdit.value) {
    if (mode.value === FormModes.view) {
      changeMode(FormModes.edit);
    } else {
      deleteAddress(address, index);
    }
  }
}

function deleteAddress(address: AddressFormInputs, index: number) {
  emit("onDelete", {
    index,
    address,
  });
}

function changeMode(newMode: keyof typeof FormModes) {
  address.value.mode = newMode;
  mode.value = newMode;
}

function updateAddressId(updatedAddress: AddressFormInputs) {
  address.value.id = updatedAddress.id || updatedAddress.addressId;
  address.value.addressId = updatedAddress.id || updatedAddress.addressId;
}

async function confirmChanges() {
  validating.value = true;
  error.value = false;

  try {		
    const validationResponse = await onValidate();

    if (validationResponse.valid) {
      await saveChanges();
    } else {
      validating.value = false;
    }
  } catch (error: any) {
    console.log("Validation failed. Errors:", (error as any)?.errors);
    validating.value = false;
    error.value = true;
  }
}

async function saveChanges() {
  savingChanges.value = true;

  try {
    const response = await saveAddressAsync();
    if (response?.success) {
      updateAddressId(response.results);
      changeMode(FormModes.view);
      showAddressDetails.value = false;
      emit("closeDialogueModal");
      savingChanges.value = false;
    } else {
      emit("onError");
      savingChanges.value = false;
    }
  } catch {
    savingChanges.value = false;
    error.value = true;
  }
}

onMounted(() => {
  mode.value = props.address.mode;
  isMounted.value = true;
});

onUnmounted(() => {
  resetForm();
  mode.value = FormModes.view;
});
watch(
  () => mode.value,
  newValue => {
    if (newValue === FormModes.view) {
      resetAddressSchema();
    }
  },
);

defineExpose({
  address,
  confirmChanges,
  error,
  formattedAddress,
  hasErrors,
  validate,
  validating,
  props,
  saveChanges,
  showAddressDetails,
});
</script>

<style scoped>
* {
  font-family: "Montserrat";
  color: #000;
  font-size: 12px;
  font-style: normal;
  line-height: 155%;
  /* 18.6px */
}

.form-wrapper {
  display: flex;
  flex-direction: column;
}

.form-wrapper.view {
  padding-bottom: 0;
}

.form-wrapper .form:not(:last-of-type) {
  margin-bottom: 16px;
}

.form {
  border: 1px solid #d4d9de;
  padding: 20px;
  border-radius: 6px;
  border: 1px solid #d4d9de;
  display: flex;
  flex-direction: column;
}

.form-header {
  padding: 0;
  color: #000;
  font-family: "Montserrat";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 155%;
  /* 24.8px */
  display: flex;
}

.form-header .title {
  flex: 1;
  padding: 0;
  color: #000;
  font-family: "Montserrat";
  font-size: 16px;
  font-style: normal;
  font-weight: 700 !important;
  line-height: 155%;
  /* 24.8px */
}

.form-header .actions {
  display: flex;
  flex: 0 0 fit-content;
  align-items: center;
}

.form-header a {
  flex: 0 0 fit-content;
  color: #000;
  text-align: right;
  font-family: "Montserrat";
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  /* 166.667% */
  cursor: pointer;
}

.form-header a:hover {
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
}

.summary {
  color: #000;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 155%;
  /* 18.6px */
}

.form-fields#address {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.form-fields#address .row {
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: 100%;
  margin-right: 0;
  margin-bottom: 6px;
}

.form-fields#address .button-wrapper button {
  align-items: center;
  border-radius: 4px;
  background: #000;
  color: #fff;
  cursor: pointer;
  display: flex;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  height: 34px !important;
  justify-content: center;
  line-height: 20px;
  /* 142.857% */
  width: 187px !important;
  padding: 11px 16px;
  gap: 10px;
}

.col,
.cuscd-full-wrap .row {
  margin-right: 0px;
  margin-left: 0px;
  padding: 0px;
}

[class^="col-"],
[class*=" col-"] {
  padding: 0 !important;
  margin-right: 0px;
  margin-left: 0px;
  padding: 0px;
}

.col-group {
  display: flex;
  width: 100%;
}

.col-group:not(:last-of-type) {
  margin-bottom: 6px;
}

.sc-button {
  background-color: var(--theme-color, #000);
  color: #fff;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  /* 142.857% */
}

.cuscd-dark .sc-button {
  color: #000100;
}

.col-group {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.col-lg-6 {
  flex: 0 0 calc(50% - 8px);
  max-width: calc(50% - 8px);
}

.footer {
  padding: 16px 0 0;
}

.details-toggle {
  display: flex;
  flex-direction: row;
  color: #000;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 155%;
  /* 15.5px */
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 2px;
}

.details-toggle:hover {
  font-style: normal;
  text-decoration: underline;
}

.icon {
  font-size: 16px;
  color: #000;
}

input[type="text"]:disabled,
input[type="number"]:disabled,
select:disabled {
  background-color: transparent !important;
  border: none !important;
  color: #000 !important;
  padding: 0 !important;
}

.tooltip-wrapper {
  display: flex;
}

.form-header .title {
  display: flex;
}
@media (max-width: 992px) {
  .form-wrapper.view .form {
    padding-bottom: 0.5rem;
  }

  .row {
    display: flex;
    width: 100%;
    margin-bottom: 1rem;
  }

  .col-10 {
    width: 100%;
  }

  .form {
    padding-top: 0.5rem;
  }
}

@media (max-width: 768px) {
  .row {
    flex-direction: column;
    gap: 1rem;
  }

  .col-mobile-12 {
    flex: 1;
    min-width: 100%;
    width: 100%;
  }

  .form-fields#address {
    flex-direction: column;
    gap: 8px;
  }

  .col-group {
    flex-wrap: wrap;
  }

  .details-toggle {
    font-size: 12px;
  }
}
</style>
